import { IsOptional } from 'class-validator';
import { JsonProperty, JsonObject, Any } from 'json2typescript';
import { Address, Collector } from '@hermes/api-model-address';
import { ModelObject } from '@hermes/api-model-core';
import { ShippingDelay, ProductLeadTime, ProductItem } from '@hermes/api-model-product';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol, Iterator */


function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

let CustomOptions = class CustomOptions {
    constructor() {
        this.engraving = undefined;
    }
};
__decorate([
    JsonProperty("engraving", [String], true),
    __metadata("design:type", Array)
], CustomOptions.prototype, "engraving", undefined);
CustomOptions = __decorate([
    JsonObject("CustomOptions")
], CustomOptions);

let BasketItemRequest = class BasketItemRequest {
    constructor() {
        this.container_id = undefined;
        this.category = undefined;
        this.qty = undefined;
        this.image = undefined;
        this.sku = undefined;
        this.dku = undefined;
        this.customOptions = undefined;
        this.isEngravable = undefined;
    }
};
__decorate([
    JsonProperty("container_id", String, true),
    __metadata("design:type", String)
], BasketItemRequest.prototype, "container_id", undefined);
__decorate([
    JsonProperty("category", String, true),
    __metadata("design:type", String)
], BasketItemRequest.prototype, "category", undefined);
__decorate([
    JsonProperty("qty", Number, true),
    __metadata("design:type", Number)
], BasketItemRequest.prototype, "qty", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], BasketItemRequest.prototype, "image", undefined);
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], BasketItemRequest.prototype, "sku", undefined);
__decorate([
    JsonProperty("dku", String, true),
    __metadata("design:type", String)
], BasketItemRequest.prototype, "dku", undefined);
__decorate([
    JsonProperty("custom_options", CustomOptions, true),
    __metadata("design:type", CustomOptions)
], BasketItemRequest.prototype, "customOptions", undefined);
__decorate([
    JsonProperty("is_engravable", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItemRequest.prototype, "isEngravable", undefined);
BasketItemRequest = __decorate([
    JsonObject("BasketItemRequest")
], BasketItemRequest);

let TrackingInformation = class TrackingInformation {
};
__decorate([
    JsonProperty("feature", String),
    __metadata("design:type", String)
], TrackingInformation.prototype, "feature", undefined);
__decorate([
    JsonProperty("information", String),
    __metadata("design:type", String)
], TrackingInformation.prototype, "information", undefined);
TrackingInformation = __decorate([
    JsonObject("TrackingInformation")
], TrackingInformation);

let AddToCartRequest = class AddToCartRequest {
};
__decorate([
    JsonProperty("items", [BasketItemRequest]),
    __metadata("design:type", Array)
], AddToCartRequest.prototype, "items", undefined);
__decorate([
    JsonProperty(),
    __metadata("design:type", String)
], AddToCartRequest.prototype, "locale", undefined);
__decorate([
    IsOptional(),
    JsonProperty("tracking", TrackingInformation),
    __metadata("design:type", TrackingInformation)
], AddToCartRequest.prototype, "trackingInformation", undefined);
AddToCartRequest = __decorate([
    JsonObject("AddToCartRequest")
], AddToCartRequest);

let BasketItemStock = class BasketItemStock extends ModelObject {
    constructor() {
        super(...arguments);
        this.isEmpty = undefined;
        this.isEqualsToQty = undefined;
        this.isLessThanQty = undefined;
    }
};
__decorate([
    JsonProperty("is_empty", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItemStock.prototype, "isEmpty", undefined);
__decorate([
    JsonProperty("is_equals_to_qty", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItemStock.prototype, "isEqualsToQty", undefined);
__decorate([
    JsonProperty("is_less_than_qty", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItemStock.prototype, "isLessThanQty", undefined);
BasketItemStock = __decorate([
    JsonObject("BasketItemStock")
], BasketItemStock);

let BasketSetItem = class BasketSetItem extends ModelObject {
    constructor() {
        super(...arguments);
        this.color = undefined;
        this.departmentCode = undefined;
        this.divisionCode = undefined;
        this.familyCode = undefined;
        this.metalTitration1 = undefined;
        this.metalTitration2 = undefined;
        this.metalTitration3 = undefined;
        this.excludedMaterial = undefined;
        this.name = undefined;
        this.productCode = undefined;
        this.prop65 = undefined;
        this.qty = undefined;
        this.size = undefined;
        this.sku = undefined;
        this.linkId = undefined;
        this.displaySingleLine = undefined;
        this.displaySingleLineOptions = undefined;
        /** Detect Casper products & desynchronized stock products */
        this.isFakeQty = undefined;
        this.isDangerousGood = undefined;
    }
};
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "color", undefined);
__decorate([
    JsonProperty("department_code", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("division_code", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("family_code", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("metal_titration1", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "metalTitration1", undefined);
__decorate([
    JsonProperty("metal_titration2", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "metalTitration2", undefined);
__decorate([
    JsonProperty("metal_titration3", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "metalTitration3", undefined);
__decorate([
    JsonProperty("excluded_material", Any, true),
    __metadata("design:type", Object)
], BasketSetItem.prototype, "excludedMaterial", undefined);
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "name", undefined);
__decorate([
    JsonProperty("product_code", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "productCode", undefined);
__decorate([
    JsonProperty("prop65", Any, true),
    __metadata("design:type", Object)
], BasketSetItem.prototype, "prop65", undefined);
__decorate([
    JsonProperty("qty", Number, true),
    __metadata("design:type", Number)
], BasketSetItem.prototype, "qty", undefined);
__decorate([
    JsonProperty("size", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "size", undefined);
__decorate([
    JsonProperty("sku", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "sku", undefined);
__decorate([
    JsonProperty("link_id", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "linkId", undefined);
__decorate([
    JsonProperty("display_single_line", Boolean, true),
    __metadata("design:type", Boolean)
], BasketSetItem.prototype, "displaySingleLine", undefined);
__decorate([
    JsonProperty("display_single_line_options", String, true),
    __metadata("design:type", String)
], BasketSetItem.prototype, "displaySingleLineOptions", undefined);
__decorate([
    JsonProperty("is_fake_qty", Boolean, true),
    __metadata("design:type", Boolean)
], BasketSetItem.prototype, "isFakeQty", undefined);
__decorate([
    JsonProperty("is_dangerous_good", Boolean, true),
    __metadata("design:type", Boolean)
], BasketSetItem.prototype, "isDangerousGood", undefined);
BasketSetItem = __decorate([
    JsonObject("BasketSetItem")
], BasketSetItem);

let ErrorItem = class ErrorItem extends ModelObject {
    constructor() {
        super(...arguments);
        this.code = undefined;
        this.internalCode = undefined;
        this.item = undefined;
        this.message = undefined;
    }
};
__decorate([
    JsonProperty("code", Number, true),
    __metadata("design:type", Number)
], ErrorItem.prototype, "code", undefined);
__decorate([
    JsonProperty("internal_code", String, true),
    __metadata("design:type", String)
], ErrorItem.prototype, "internalCode", undefined);
__decorate([
    JsonProperty("item", String, true),
    __metadata("design:type", String)
], ErrorItem.prototype, "item", undefined);
__decorate([
    JsonProperty("message", String, true),
    __metadata("design:type", String)
], ErrorItem.prototype, "message", undefined);
ErrorItem = __decorate([
    JsonObject("ErrorItem")
], ErrorItem);

let BasketItem = class BasketItem extends ProductItem {
    constructor() {
        super(...arguments);
        this.color = undefined;
        this.colorCode = undefined;
        this.departmentCode = undefined;
        this.displaySingleLine = undefined;
        this.displaySingleLineSku = undefined;
        this.displaySingleLineOptions = undefined;
        this.divisionCode = undefined;
        this.errorItem = undefined;
        this.familyCode = undefined;
        this.finalPrice = undefined;
        this.hasError = undefined;
        this.hasSharedStock = undefined;
        this.image = undefined;
        this.itemOrderId = undefined;
        this.novaItemId = undefined;
        this.linkId = undefined;
        this.max_sale_qty = undefined;
        this.metalTitration1 = undefined;
        this.metalTitration2 = undefined;
        this.metalTitration3 = undefined;
        this.excludedMaterial = undefined;
        this.name = undefined;
        this.options = undefined;
        this.priceInclTax = undefined;
        this.prop65 = undefined;
        this.qty = undefined;
        this.qty_increments = undefined;
        this.setItems = undefined;
        this.shippingInformation = undefined;
        this.size = undefined;
        this.unitPrice = undefined;
        this.url = undefined;
        this.dku = undefined;
        /** Detect Casper products & desynchronized stock products */
        this.isFakeQty = undefined;
        this.stock = undefined;
        this.shippingDelay = undefined;
        this.isDangerousGood = undefined;
        this.leadTime = undefined;
        this.customOptions = undefined;
        this.isEngravable = undefined;
    }
};
__decorate([
    JsonProperty("color", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "color", undefined);
__decorate([
    JsonProperty("color_code", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "colorCode", undefined);
__decorate([
    JsonProperty("department_code", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "departmentCode", undefined);
__decorate([
    JsonProperty("display_single_line", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "displaySingleLine", undefined);
__decorate([
    JsonProperty("display_single_line_sku", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "displaySingleLineSku", undefined);
__decorate([
    JsonProperty("display_single_line_options", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "displaySingleLineOptions", undefined);
__decorate([
    JsonProperty("division_code", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "divisionCode", undefined);
__decorate([
    JsonProperty("error_item", ErrorItem, true),
    __metadata("design:type", ErrorItem)
], BasketItem.prototype, "errorItem", undefined);
__decorate([
    JsonProperty("family_code", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "familyCode", undefined);
__decorate([
    JsonProperty("final_price", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "finalPrice", undefined);
__decorate([
    JsonProperty("has_error", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "hasError", undefined);
__decorate([
    JsonProperty("has_shared_stock", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "hasSharedStock", undefined);
__decorate([
    JsonProperty("image", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "image", undefined);
__decorate([
    JsonProperty("item_order_id", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "itemOrderId", undefined);
__decorate([
    JsonProperty("nova_item_id", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "novaItemId", undefined);
__decorate([
    JsonProperty("link_id", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "linkId", undefined);
__decorate([
    JsonProperty("max_sale_qty", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "max_sale_qty", undefined);
__decorate([
    JsonProperty("metal_titration1", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "metalTitration1", undefined);
__decorate([
    JsonProperty("metal_titration2", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "metalTitration2", undefined);
__decorate([
    JsonProperty("metal_titration3", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "metalTitration3", undefined);
__decorate([
    JsonProperty("excluded_material", Any, true),
    __metadata("design:type", Object)
], BasketItem.prototype, "excludedMaterial", undefined);
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "name", undefined);
__decorate([
    JsonProperty("options", Any, true),
    __metadata("design:type", Object)
], BasketItem.prototype, "options", undefined);
__decorate([
    JsonProperty("price_incl_tax", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "priceInclTax", undefined);
__decorate([
    JsonProperty("prop65", Any, true),
    __metadata("design:type", Object)
], BasketItem.prototype, "prop65", undefined);
__decorate([
    JsonProperty("qty", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "qty", undefined);
__decorate([
    JsonProperty("qty_increments", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "qty_increments", undefined);
__decorate([
    JsonProperty("set_items", [BasketSetItem], true),
    __metadata("design:type", Array)
], BasketItem.prototype, "setItems", undefined);
__decorate([
    JsonProperty("shipping_information", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "shippingInformation", undefined);
__decorate([
    JsonProperty("size", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "size", undefined);
__decorate([
    JsonProperty("unit_price", Number, true),
    __metadata("design:type", Number)
], BasketItem.prototype, "unitPrice", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "url", undefined);
__decorate([
    JsonProperty("dku", String, true),
    __metadata("design:type", String)
], BasketItem.prototype, "dku", undefined);
__decorate([
    JsonProperty("is_fake_qty", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "isFakeQty", undefined);
__decorate([
    JsonProperty("stock", BasketItemStock, true),
    __metadata("design:type", BasketItemStock)
], BasketItem.prototype, "stock", undefined);
__decorate([
    JsonProperty("shipping_delay", ShippingDelay, true),
    __metadata("design:type", ShippingDelay)
], BasketItem.prototype, "shippingDelay", undefined);
__decorate([
    JsonProperty("is_dangerous_good", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "isDangerousGood", undefined);
__decorate([
    JsonProperty("lead_time", ProductLeadTime, true),
    __metadata("design:type", ProductLeadTime)
], BasketItem.prototype, "leadTime", undefined);
__decorate([
    JsonProperty("custom_options", CustomOptions, true),
    __metadata("design:type", CustomOptions)
], BasketItem.prototype, "customOptions", undefined);
__decorate([
    JsonProperty("is_engravable", Boolean, true),
    __metadata("design:type", Boolean)
], BasketItem.prototype, "isEngravable", undefined);
BasketItem = __decorate([
    JsonObject("BasketItem")
], BasketItem);

let EcpayDonation = class EcpayDonation {
    constructor() {
        this.donationCode = undefined;
        this.name = undefined;
    }
};
__decorate([
    JsonProperty("donation_code", String, true),
    __metadata("design:type", String)
], EcpayDonation.prototype, "donationCode", undefined);
__decorate([
    JsonProperty("name", String, true),
    __metadata("design:type", String)
], EcpayDonation.prototype, "name", undefined);
EcpayDonation = __decorate([
    JsonObject("EcpayDonation")
], EcpayDonation);

let Gift = class Gift {
    constructor() {
        this.giftCardType = "empty";
        this.handwrittenInvoice = false;
        this.hidePrice = false;
        this.isGift = false;
        this.message = "";
    }
};
__decorate([
    JsonProperty("gift_card_type", String, true),
    __metadata("design:type", String)
], Gift.prototype, "giftCardType", undefined);
__decorate([
    JsonProperty("handwritten", Boolean, true),
    __metadata("design:type", Boolean)
], Gift.prototype, "handwrittenInvoice", undefined);
__decorate([
    JsonProperty("hide_price", Boolean, true),
    __metadata("design:type", Boolean)
], Gift.prototype, "hidePrice", undefined);
__decorate([
    JsonProperty("is_gift", Boolean, true),
    __metadata("design:type", Boolean)
], Gift.prototype, "isGift", undefined);
__decorate([
    JsonProperty("message", String, true),
    __metadata("design:type", String)
], Gift.prototype, "message", undefined);
Gift = __decorate([
    JsonObject("Gift")
], Gift);

let ShowTax = class ShowTax extends ModelObject {
    constructor() {
        super(...arguments);
        this.all_en = undefined;
        this.at_de = undefined;
        this.au_en = undefined;
        this.be_en = undefined;
        this.be_fr = undefined;
        this.br_en = undefined;
        this.ca_en = undefined;
        this.ca_fr = undefined;
        this.ch_de = undefined;
        this.ch_fr = undefined;
        this.cn_en = undefined;
        this.cn_zh = undefined;
        this.cz_en = undefined;
        this.de_de = undefined;
        this.dk_en = undefined;
        this.es_es = undefined;
        this.fi_en = undefined;
        this.fr_fr = undefined;
        this.frx_fr = undefined;
        this.he_en = undefined;
        this.he_fr = undefined;
        this.he_ja = undefined;
        this.hk_en = undefined;
        this.ie_en = undefined;
        this.it_en = undefined;
        this.it_it = undefined;
        this.jp_ja = undefined;
        this.kr_ko = undefined;
        this.lu_fr = undefined;
        this.me_en = undefined;
        this.mo_en = undefined;
        this.mx_es = undefined;
        this.my_en = undefined;
        this.nl_en = undefined;
        this.no_en = undefined;
        this.nz_en = undefined;
        this.pl_en = undefined;
        this.pt_en = undefined;
        this.ru_ru = undefined;
        this.se_en = undefined;
        this.sg_en = undefined;
        this.so_en = undefined;
        this.tw_en = undefined;
        this.tw_zh = undefined;
        this.uk_en = undefined;
        this.us_en = undefined;
        this.br_pt = undefined;
        this.dh_en = undefined;
        this.ri_en = undefined;
        this.th_en = undefined;
    }
};
__decorate([
    JsonProperty("all_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "all_en", undefined);
__decorate([
    JsonProperty("at_de", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "at_de", undefined);
__decorate([
    JsonProperty("au_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "au_en", undefined);
__decorate([
    JsonProperty("be_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "be_en", undefined);
__decorate([
    JsonProperty("be_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "be_fr", undefined);
__decorate([
    JsonProperty("br_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "br_en", undefined);
__decorate([
    JsonProperty("ca_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ca_en", undefined);
__decorate([
    JsonProperty("ca_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ca_fr", undefined);
__decorate([
    JsonProperty("ch_de", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ch_de", undefined);
__decorate([
    JsonProperty("ch_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ch_fr", undefined);
__decorate([
    JsonProperty("cn_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "cn_en", undefined);
__decorate([
    JsonProperty("cn_zh", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "cn_zh", undefined);
__decorate([
    JsonProperty("cz_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "cz_en", undefined);
__decorate([
    JsonProperty("de_de", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "de_de", undefined);
__decorate([
    JsonProperty("dk_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "dk_en", undefined);
__decorate([
    JsonProperty("es_es", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "es_es", undefined);
__decorate([
    JsonProperty("fi_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "fi_en", undefined);
__decorate([
    JsonProperty("fr_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "fr_fr", undefined);
__decorate([
    JsonProperty("frx_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "frx_fr", undefined);
__decorate([
    JsonProperty("he_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "he_en", undefined);
__decorate([
    JsonProperty("he_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "he_fr", undefined);
__decorate([
    JsonProperty("he_ja", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "he_ja", undefined);
__decorate([
    JsonProperty("hk_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "hk_en", undefined);
__decorate([
    JsonProperty("ie_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ie_en", undefined);
__decorate([
    JsonProperty("it_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "it_en", undefined);
__decorate([
    JsonProperty("it_it", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "it_it", undefined);
__decorate([
    JsonProperty("jp_ja", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "jp_ja", undefined);
__decorate([
    JsonProperty("kr_ko", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "kr_ko", undefined);
__decorate([
    JsonProperty("lu_fr", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "lu_fr", undefined);
__decorate([
    JsonProperty("me_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "me_en", undefined);
__decorate([
    JsonProperty("mo_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "mo_en", undefined);
__decorate([
    JsonProperty("mx_es", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "mx_es", undefined);
__decorate([
    JsonProperty("my_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "my_en", undefined);
__decorate([
    JsonProperty("nl_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "nl_en", undefined);
__decorate([
    JsonProperty("no_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "no_en", undefined);
__decorate([
    JsonProperty("nz_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "nz_en", undefined);
__decorate([
    JsonProperty("pl_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "pl_en", undefined);
__decorate([
    JsonProperty("pt_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "pt_en", undefined);
__decorate([
    JsonProperty("ru_ru", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ru_ru", undefined);
__decorate([
    JsonProperty("se_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "se_en", undefined);
__decorate([
    JsonProperty("sg_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "sg_en", undefined);
__decorate([
    JsonProperty("so_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "so_en", undefined);
__decorate([
    JsonProperty("tw_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "tw_en", undefined);
__decorate([
    JsonProperty("tw_zh", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "tw_zh", undefined);
__decorate([
    JsonProperty("uk_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "uk_en", undefined);
__decorate([
    JsonProperty("us_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "us_en", undefined);
__decorate([
    JsonProperty("br_pt", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "br_pt", undefined);
__decorate([
    JsonProperty("dh_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "dh_en", undefined);
__decorate([
    JsonProperty("ri_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "ri_en", undefined);
__decorate([
    JsonProperty("th_en", Number, true),
    __metadata("design:type", Number)
], ShowTax.prototype, "th_en", undefined);
ShowTax = __decorate([
    JsonObject("ShowTax")
], ShowTax);

/**
 * This class describes the schema for a basket
 * @dynamic
 */
let Basket = class Basket extends ModelObject {
    constructor(source) {
        super();
        /**
         * Shipping properties.
         */
        this.availableShippingMethod = undefined;
        this.billingAddress = undefined;
        this.default_shipping_method = undefined;
        this.estimateTaxError = undefined;
        this.gift = undefined;
        this.hasSample = undefined;
        this.hasFamilyCode = undefined;
        this.sampleSkus = [];
        this.hasProductThreshold = undefined;
        this.grandTotal = undefined;
        this.hasCasper = undefined;
        this.hasSharedStock = undefined;
        this.items = undefined;
        this.shippingAddress = undefined;
        this.shippingAmount = undefined;
        this.shippingDuration = undefined;
        this.shippingMethod = undefined;
        this.showMessage = undefined;
        this.showTax = undefined;
        this.showTaxMessage = undefined;
        this.subtotal = undefined;
        this.taxAmount = undefined;
        this.totalItems = undefined;
        this.hasComplimentaryDelivery = undefined;
        this.hasPreOrder = undefined;
        this.hasRecurring = undefined;
        this.authorizeUrl = undefined;
        this.taxesCovered = undefined;
        this.isPersonalized = undefined;
        this.shippingAppointmentTimeslots = undefined;
        this.einvoiceNgoList = undefined;
        this.regimenFiscalList = undefined;
        this.usoCfdiList = undefined;
        Object.assign(this, source);
    }
    // Check if basket is empty
    isEmpty() {
        return !this.totalItems || this.totalItems < 1;
    }
};
__decorate([
    JsonProperty("available_shipping_methods", Any, true),
    __metadata("design:type", Object)
], Basket.prototype, "availableShippingMethod", undefined);
__decorate([
    JsonProperty("billing_address", Address, true),
    __metadata("design:type", Address)
], Basket.prototype, "billingAddress", undefined);
__decorate([
    JsonProperty("default_shipping_method", String, true),
    __metadata("design:type", String)
], Basket.prototype, "default_shipping_method", undefined);
__decorate([
    JsonProperty("estimate_tax_error", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "estimateTaxError", undefined);
__decorate([
    JsonProperty("gift", Gift, true),
    __metadata("design:type", Gift)
], Basket.prototype, "gift", undefined);
__decorate([
    JsonProperty("has_sample", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasSample", undefined);
__decorate([
    JsonProperty("has_family_code", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasFamilyCode", undefined);
__decorate([
    JsonProperty("sample_skus", [String], true),
    __metadata("design:type", Array)
], Basket.prototype, "sampleSkus", undefined);
__decorate([
    JsonProperty("has_product_threshold", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasProductThreshold", undefined);
__decorate([
    JsonProperty("grand_total", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "grandTotal", undefined);
__decorate([
    JsonProperty("has_casper", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasCasper", undefined);
__decorate([
    JsonProperty("has_shared_stock", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasSharedStock", undefined);
__decorate([
    JsonProperty("items", [BasketItem], true),
    __metadata("design:type", Array)
], Basket.prototype, "items", undefined);
__decorate([
    JsonProperty("shipping_address", Address, true),
    __metadata("design:type", Address)
], Basket.prototype, "shippingAddress", undefined);
__decorate([
    JsonProperty("shipping_amount", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "shippingAmount", undefined);
__decorate([
    JsonProperty("shipping_duration", String, true),
    __metadata("design:type", String)
], Basket.prototype, "shippingDuration", undefined);
__decorate([
    JsonProperty("shipping_method", String, true),
    __metadata("design:type", String)
], Basket.prototype, "shippingMethod", undefined);
__decorate([
    JsonProperty("show_message", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "showMessage", undefined);
__decorate([
    JsonProperty("show_tax", ShowTax, true),
    __metadata("design:type", ShowTax)
], Basket.prototype, "showTax", undefined);
__decorate([
    JsonProperty("show_tax_message", Any, true),
    __metadata("design:type", Object)
], Basket.prototype, "showTaxMessage", undefined);
__decorate([
    JsonProperty("subtotal", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "subtotal", undefined);
__decorate([
    JsonProperty("tax_amount", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "taxAmount", undefined);
__decorate([
    JsonProperty("total_items", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "totalItems", undefined);
__decorate([
    JsonProperty("has_complimentary_delivery", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasComplimentaryDelivery", undefined);
__decorate([
    JsonProperty("has_pre_order", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasPreOrder", undefined);
__decorate([
    JsonProperty("has_recurring", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "hasRecurring", undefined);
__decorate([
    JsonProperty("authorize_url", String, true),
    __metadata("design:type", String)
], Basket.prototype, "authorizeUrl", undefined);
__decorate([
    JsonProperty("taxes_covered", Number, true),
    __metadata("design:type", Number)
], Basket.prototype, "taxesCovered", undefined);
__decorate([
    JsonProperty("is_personalized", Boolean, true),
    __metadata("design:type", Boolean)
], Basket.prototype, "isPersonalized", undefined);
__decorate([
    JsonProperty("shipping_appointment_timeslots", Any, true),
    __metadata("design:type", Object)
], Basket.prototype, "shippingAppointmentTimeslots", undefined);
__decorate([
    JsonProperty("einvoice_ngo_list", [EcpayDonation], true),
    __metadata("design:type", Array)
], Basket.prototype, "einvoiceNgoList", undefined);
__decorate([
    JsonProperty("regimen_fiscal_list", Any, true),
    __metadata("design:type", Object)
], Basket.prototype, "regimenFiscalList", undefined);
__decorate([
    JsonProperty("uso_cfdi_list", Any, true),
    __metadata("design:type", Object)
], Basket.prototype, "usoCfdiList", undefined);
Basket = __decorate([
    JsonObject("Basket"),
    __metadata("design:paramtypes", [Object])
], Basket);

let AddToCartResponse = class AddToCartResponse {
    constructor() {
        this.basket = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], AddToCartResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("error", Any, true),
    __metadata("design:type", Object)
], AddToCartResponse.prototype, "error", undefined);
AddToCartResponse = __decorate([
    JsonObject("AddToCartResponse")
], AddToCartResponse);

/**
 * This class define the users attributes
 */
let WebToShopDetails = class WebToShopDetails extends ModelObject {
    constructor() {
        super(...arguments);
        this.city = undefined;
        this.company = undefined;
        this.countryId = undefined;
        this.district = undefined;
        this.districtBisId = undefined;
        this.fax = undefined;
        this.latitude = undefined;
        this.longitude = undefined;
        this.opening = undefined;
        this.postcode = undefined;
        this.region = undefined;
        this.regionId = undefined;
        this.regionLabel = undefined;
        this.requiresCollector = undefined;
        this.shippingDuration = undefined;
        this.shippingMethod = undefined;
        this.shopId = undefined;
        this.streetNumber = undefined;
        this.street1 = undefined;
        this.street2 = undefined;
        this.street3 = undefined;
        this.telephone = undefined;
        this.url = undefined;
        this.calculatedDistance = undefined;
        this.calculatedDistanceHumanReadable = undefined;
        this.additionalNumber = undefined;
        this.neighborhood = undefined;
    }
};
__decorate([
    JsonProperty("city", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "city", undefined);
__decorate([
    JsonProperty("company", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "company", undefined);
__decorate([
    JsonProperty("country_id", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "countryId", undefined);
__decorate([
    JsonProperty("district", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "district", undefined);
__decorate([
    JsonProperty("district_bis_id", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "districtBisId", undefined);
__decorate([
    JsonProperty("fax", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "fax", undefined);
__decorate([
    JsonProperty("latitude", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "latitude", undefined);
__decorate([
    JsonProperty("longitude", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "longitude", undefined);
__decorate([
    JsonProperty("opening", [[String]], true),
    __metadata("design:type", Array)
], WebToShopDetails.prototype, "opening", undefined);
__decorate([
    JsonProperty("postcode", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "postcode", undefined);
__decorate([
    JsonProperty("region", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "region", undefined);
__decorate([
    JsonProperty("region_id", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "regionId", undefined);
__decorate([
    JsonProperty("region_label", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "regionLabel", undefined);
__decorate([
    JsonProperty("requires_collector", Boolean, true),
    __metadata("design:type", Boolean)
], WebToShopDetails.prototype, "requiresCollector", undefined);
__decorate([
    JsonProperty("shipping_duration", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "shippingDuration", undefined);
__decorate([
    JsonProperty("shipping_method", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "shippingMethod", undefined);
__decorate([
    JsonProperty("shop_id", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "shopId", undefined);
__decorate([
    JsonProperty("street_number", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "streetNumber", undefined);
__decorate([
    JsonProperty("street1", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "street1", undefined);
__decorate([
    JsonProperty("street2", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "street2", undefined);
__decorate([
    JsonProperty("street3", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "street3", undefined);
__decorate([
    JsonProperty("telephone", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "telephone", undefined);
__decorate([
    JsonProperty("url", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "url", undefined);
__decorate([
    JsonProperty("calculatedDistance", Number, true),
    __metadata("design:type", Number)
], WebToShopDetails.prototype, "calculatedDistance", undefined);
__decorate([
    JsonProperty("calculatedDistanceHumanReadable", Number, true),
    __metadata("design:type", Number)
], WebToShopDetails.prototype, "calculatedDistanceHumanReadable", undefined);
__decorate([
    JsonProperty("additional_number", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "additionalNumber", undefined);
__decorate([
    JsonProperty("neighborhood", String, true),
    __metadata("design:type", String)
], WebToShopDetails.prototype, "neighborhood", undefined);
WebToShopDetails = __decorate([
    JsonObject("Shop")
], WebToShopDetails);

let DeliveryResponse = class DeliveryResponse extends ModelObject {
    constructor() {
        super(...arguments);
        this.basket = undefined;
        this.shops = undefined;
        this.webToShopAvailable = undefined;
        this.shippingAddresses = undefined;
        this.availableCountries = undefined;
        this.lastWebToShop = undefined;
        this.isGuestCaptchaActivated = false;
        this.isLoginCaptchaActivated = false;
    }
};
__decorate([
    JsonProperty("basket", Basket, true),
    __metadata("design:type", Basket)
], DeliveryResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("shops", [WebToShopDetails], true),
    __metadata("design:type", Array)
], DeliveryResponse.prototype, "shops", undefined);
__decorate([
    JsonProperty("webToShopAvailable", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryResponse.prototype, "webToShopAvailable", undefined);
__decorate([
    JsonProperty("shippingAddresses", [Address], true),
    __metadata("design:type", Array)
], DeliveryResponse.prototype, "shippingAddresses", undefined);
__decorate([
    JsonProperty("available_countries", [String], true),
    __metadata("design:type", Array)
], DeliveryResponse.prototype, "availableCountries", undefined);
__decorate([
    JsonProperty("mostRecent", String, true),
    __metadata("design:type", String)
], DeliveryResponse.prototype, "lastWebToShop", undefined);
__decorate([
    JsonProperty("isGuestCaptchaActivated", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryResponse.prototype, "isGuestCaptchaActivated", undefined);
__decorate([
    JsonProperty("isLoginCaptchaActivated", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryResponse.prototype, "isLoginCaptchaActivated", undefined);
DeliveryResponse = __decorate([
    JsonObject("DeliveryResponse")
], DeliveryResponse);

let DeliveryAddressRequest = class DeliveryAddressRequest {
    constructor() {
        this.is_apple_pay = undefined;
        this.is_known_but_not_connected = undefined;
        this.is_known_but_not_connected_email = undefined;
        this.is_first_apple_pay_address = undefined;
    }
};
__decorate([
    JsonProperty(),
    __metadata("design:type", String)
], DeliveryAddressRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("customer_address_id", String),
    __metadata("design:type", String)
], DeliveryAddressRequest.prototype, "customer_address_id", undefined);
__decorate([
    JsonProperty("address_type", String),
    __metadata("design:type", String)
], DeliveryAddressRequest.prototype, "address_type", undefined);
__decorate([
    JsonProperty("is_apple_pay", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryAddressRequest.prototype, "is_apple_pay", undefined);
__decorate([
    JsonProperty("is_known_but_not_connected", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryAddressRequest.prototype, "is_known_but_not_connected", undefined);
__decorate([
    JsonProperty("is_known_but_not_connected_email", String, true),
    __metadata("design:type", String)
], DeliveryAddressRequest.prototype, "is_known_but_not_connected_email", undefined);
__decorate([
    JsonProperty("is_first_apple_pay_address", Boolean, true),
    __metadata("design:type", Boolean)
], DeliveryAddressRequest.prototype, "is_first_apple_pay_address", undefined);
DeliveryAddressRequest = __decorate([
    JsonObject("DeliveryAddressRequest")
], DeliveryAddressRequest);

let DeliveryBlockerContext = class DeliveryBlockerContext extends ModelObject {
    constructor() {
        super(...arguments);
        this.code = undefined;
        this.label = undefined;
    }
};
__decorate([
    JsonProperty("code", String),
    __metadata("design:type", String)
], DeliveryBlockerContext.prototype, "code", undefined);
__decorate([
    JsonProperty("label", String),
    __metadata("design:type", String)
], DeliveryBlockerContext.prototype, "label", undefined);
DeliveryBlockerContext = __decorate([
    JsonObject("DeliveryBlockerContext")
], DeliveryBlockerContext);

let DeliveryAddressResponse = class DeliveryAddressResponse extends ModelObject {
    constructor() {
        super(...arguments);
        this.basket = undefined;
        this.deliveryBlockerContext = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], DeliveryAddressResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("deliveryBlockerContext", DeliveryBlockerContext, true),
    __metadata("design:type", DeliveryBlockerContext)
], DeliveryAddressResponse.prototype, "deliveryBlockerContext", undefined);
__decorate([
    JsonProperty("error", ErrorItem, true),
    __metadata("design:type", ErrorItem)
], DeliveryAddressResponse.prototype, "error", undefined);
DeliveryAddressResponse = __decorate([
    JsonObject("DeliveryAddressResponse")
], DeliveryAddressResponse);

let GiftRequest = class GiftRequest {
    constructor(gift) {
        this.giftCardType = "";
        this.handwrittenInvoice = false;
        this.hidePrice = false;
        this.isGift = false;
        this.message = "";
        this.giftCardType = gift.isGift ? gift.giftCardType : "empty";
        this.handwrittenInvoice = !!gift.handwrittenInvoice;
        this.hidePrice = !!gift.hidePrice;
        this.isGift = !!gift.isGift;
        this.message = gift.message || "";
    }
};
__decorate([
    JsonProperty("gift_card_type"),
    __metadata("design:type", String)
], GiftRequest.prototype, "giftCardType", undefined);
__decorate([
    JsonProperty("handwritten"),
    __metadata("design:type", Boolean)
], GiftRequest.prototype, "handwrittenInvoice", undefined);
__decorate([
    JsonProperty("hide_price"),
    __metadata("design:type", Boolean)
], GiftRequest.prototype, "hidePrice", undefined);
__decorate([
    JsonProperty("is_gift"),
    __metadata("design:type", Boolean)
], GiftRequest.prototype, "isGift", undefined);
__decorate([
    JsonProperty("message"),
    __metadata("design:type", String)
], GiftRequest.prototype, "message", undefined);
GiftRequest = __decorate([
    JsonObject("GiftRequest"),
    __metadata("design:paramtypes", [Gift])
], GiftRequest);

let DeliveryGiftRequest = class DeliveryGiftRequest {
};
__decorate([
    JsonProperty("locale"),
    __metadata("design:type", String)
], DeliveryGiftRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("gift"),
    __metadata("design:type", GiftRequest)
], DeliveryGiftRequest.prototype, "gift", undefined);
DeliveryGiftRequest = __decorate([
    JsonObject("DeliveryGiftRequest")
], DeliveryGiftRequest);

let DeliveryGiftResponse = class DeliveryGiftResponse {
    constructor() {
        this.basket = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], DeliveryGiftResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("error"),
    __metadata("design:type", Object)
], DeliveryGiftResponse.prototype, "error", undefined);
DeliveryGiftResponse = __decorate([
    JsonObject("DeliveryGiftResponse")
], DeliveryGiftResponse);

let DeliveryShippingRequest = class DeliveryShippingRequest {
};
__decorate([
    JsonProperty("shipping_method"),
    __metadata("design:type", String)
], DeliveryShippingRequest.prototype, "shipping_method", undefined);
__decorate([
    JsonProperty(),
    __metadata("design:type", String)
], DeliveryShippingRequest.prototype, "locale", undefined);
DeliveryShippingRequest = __decorate([
    JsonObject("DeliveryShippingRequest")
], DeliveryShippingRequest);

let DeliveryShippingResponse = class DeliveryShippingResponse {
    constructor() {
        this.basket = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], DeliveryShippingResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("error", ErrorItem),
    __metadata("design:type", ErrorItem)
], DeliveryShippingResponse.prototype, "error", undefined);
DeliveryShippingResponse = __decorate([
    JsonObject("DeliveryShippingResponse")
], DeliveryShippingResponse);

let DeliverySampleRequest = class DeliverySampleRequest {
    constructor() {
        this.locale = undefined;
        this.hasSample = undefined;
    }
};
__decorate([
    JsonProperty("locale", String),
    __metadata("design:type", String)
], DeliverySampleRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("has_sample", Boolean),
    __metadata("design:type", Boolean)
], DeliverySampleRequest.prototype, "hasSample", undefined);
DeliverySampleRequest = __decorate([
    JsonObject("DeliverySampleRequest")
], DeliverySampleRequest);

let DeliverySampleResponse = class DeliverySampleResponse {
    constructor() {
        this.hasSample = undefined;
    }
};
__decorate([
    JsonProperty("hasSample", Boolean, false),
    __metadata("design:type", Boolean)
], DeliverySampleResponse.prototype, "hasSample", undefined);
DeliverySampleResponse = __decorate([
    JsonObject("DeliverySampleResponse")
], DeliverySampleResponse);

let RemoveFromCartRequest = class RemoveFromCartRequest {
};
__decorate([
    JsonProperty("items", [BasketItemRequest]),
    __metadata("design:type", Array)
], RemoveFromCartRequest.prototype, "items", undefined);
__decorate([
    JsonProperty(),
    __metadata("design:type", String)
], RemoveFromCartRequest.prototype, "locale", undefined);
RemoveFromCartRequest = __decorate([
    JsonObject("RemoveFromCartRequest")
], RemoveFromCartRequest);

let RemoveFromCartResponse = class RemoveFromCartResponse {
    constructor() {
        this.basket = undefined;
        this.deleted_items = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], RemoveFromCartResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("deleted_items", [BasketItem]),
    __metadata("design:type", Array)
], RemoveFromCartResponse.prototype, "deleted_items", undefined);
__decorate([
    JsonProperty("error"),
    __metadata("design:type", Object)
], RemoveFromCartResponse.prototype, "error", undefined);
RemoveFromCartResponse = __decorate([
    JsonObject("RemoveFromCartResponse")
], RemoveFromCartResponse);

let SetCartItemRequest = class SetCartItemRequest {
};
__decorate([
    JsonProperty("items", [BasketItemRequest]),
    __metadata("design:type", Array)
], SetCartItemRequest.prototype, "items", undefined);
__decorate([
    JsonProperty(),
    __metadata("design:type", String)
], SetCartItemRequest.prototype, "locale", undefined);
SetCartItemRequest = __decorate([
    JsonObject("SetCartItemRequest")
], SetCartItemRequest);

let SetCartItemResponse = class SetCartItemResponse {
    constructor() {
        this.basket = undefined;
        this.error = undefined;
    }
};
__decorate([
    JsonProperty("basket", Basket),
    __metadata("design:type", Basket)
], SetCartItemResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("error", ErrorItem),
    __metadata("design:type", ErrorItem)
], SetCartItemResponse.prototype, "error", undefined);
SetCartItemResponse = __decorate([
    JsonObject("SetCartItemResponse")
], SetCartItemResponse);

class Shipping extends ModelObject {
    constructor(duration, name, price, title) {
        super();
        this.duration = duration;
        this.name = name;
        this.price = price;
        this.title = title;
    }
}

let ShippingTimeslot = class ShippingTimeslot extends ModelObject {
    constructor() {
        super(...arguments);
        this.deliverySlotCode = undefined;
        this.deliveryDate = undefined;
        this.endHour = undefined;
        this.endMinutes = undefined;
        this.startHour = undefined;
        this.startMinutes = undefined;
        this.dayOfWeek = undefined;
        this.tariffLevel = undefined;
        this.status = undefined;
        this.note = undefined;
        this.incentiveFlag = undefined;
        this.rawRank = undefined;
        this.rank = undefined;
        this.transactionId = undefined;
    }
};
__decorate([
    JsonProperty("deliverySlotCode", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "deliverySlotCode", undefined);
__decorate([
    JsonProperty("deliveryDate", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "deliveryDate", undefined);
__decorate([
    JsonProperty("endHour", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "endHour", undefined);
__decorate([
    JsonProperty("endMinutes", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "endMinutes", undefined);
__decorate([
    JsonProperty("startHour", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "startHour", undefined);
__decorate([
    JsonProperty("startMinutes", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "startMinutes", undefined);
__decorate([
    JsonProperty("dayOfWeek", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "dayOfWeek", undefined);
__decorate([
    JsonProperty("tariffLevel", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "tariffLevel", undefined);
__decorate([
    JsonProperty("status", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "status", undefined);
__decorate([
    JsonProperty("note", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "note", undefined);
__decorate([
    JsonProperty("incentiveFlag", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "incentiveFlag", undefined);
__decorate([
    JsonProperty("rawRank", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "rawRank", undefined);
__decorate([
    JsonProperty("rank", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "rank", undefined);
__decorate([
    JsonProperty("transactionId", String, true),
    __metadata("design:type", String)
], ShippingTimeslot.prototype, "transactionId", undefined);
ShippingTimeslot = __decorate([
    JsonObject("ShippingTimeslot")
], ShippingTimeslot);

let AppointmentInformation = class AppointmentInformation {
    constructor() {
        this.appointmentDate = undefined;
        this.appointmentTimeSlot = undefined;
    }
};
__decorate([
    JsonProperty("appointment_date"),
    __metadata("design:type", Object)
], AppointmentInformation.prototype, "appointmentDate", undefined);
__decorate([
    JsonProperty("appointment_time_slot"),
    __metadata("design:type", Object)
], AppointmentInformation.prototype, "appointmentTimeSlot", undefined);
AppointmentInformation = __decorate([
    JsonObject("AppointmentInformation")
], AppointmentInformation);

let PersistRequest = class PersistRequest extends ModelObject {
    constructor() {
        super(...arguments);
        this.locale = undefined;
        this.collector = undefined;
        this.additionalInformations = undefined;
        this.appointmentInformations = undefined;
    }
};
__decorate([
    JsonProperty("locale", String, true),
    __metadata("design:type", String)
], PersistRequest.prototype, "locale", undefined);
__decorate([
    JsonProperty("collector", Collector, true),
    __metadata("design:type", Collector)
], PersistRequest.prototype, "collector", undefined);
__decorate([
    JsonProperty("additional_informations", String, true),
    __metadata("design:type", String)
], PersistRequest.prototype, "additionalInformations", undefined);
__decorate([
    JsonProperty("appointment_informations", AppointmentInformation, true),
    __metadata("design:type", AppointmentInformation)
], PersistRequest.prototype, "appointmentInformations", undefined);
PersistRequest = __decorate([
    JsonObject("PersistRequest")
], PersistRequest);

let PersistResponse = class PersistResponse {
};
__decorate([
    JsonProperty("basket", Basket, true),
    __metadata("design:type", Basket)
], PersistResponse.prototype, "basket", undefined);
__decorate([
    JsonProperty("error", String, true),
    __metadata("design:type", String)
], PersistResponse.prototype, "error", undefined);
PersistResponse = __decorate([
    JsonObject("PersistResponse")
], PersistResponse);

export { AddToCartRequest, AddToCartResponse, AppointmentInformation, Basket, BasketItem, BasketItemRequest, BasketItemStock, BasketSetItem, CustomOptions, DeliveryAddressRequest, DeliveryAddressResponse, DeliveryBlockerContext, DeliveryGiftRequest, DeliveryGiftResponse, DeliveryResponse, DeliverySampleRequest, DeliverySampleResponse, DeliveryShippingRequest, DeliveryShippingResponse, EcpayDonation, ErrorItem, Gift, GiftRequest, PersistRequest, PersistResponse, RemoveFromCartRequest, RemoveFromCartResponse, SetCartItemRequest, SetCartItemResponse, Shipping, ShippingTimeslot, ShowTax, TrackingInformation, WebToShopDetails };
